import { 
  Document, 
  Paragraph, 
  TextRun, 
  HeadingLevel, 
  AlignmentType, 
  convertInchesToTwip, 
  Packer,
  Table,
  TableCell,
  TableRow,
  WidthType,
  BorderStyle,
  Tab,
  TabStopType,
  TabStopPosition,
  INumberingOptions,
  LevelFormat
} from 'docx'
import { saveAs } from 'file-saver'
import { parseTrainingGuide } from './documentation-parser'

// Default font settings
const DEFAULT_FONT = 'Arial'
const DEFAULT_SIZE = 22 // 11pt in half-points

// Create numbering configuration for lists
const numberingConfig: INumberingOptions = {
  config: [
    {
      reference: 'numbered-list',
      levels: [
        {
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
            },
          },
        },
      ],
    },
    {
      reference: 'bullet-list',
      levels: [
        {
          level: 0,
          format: LevelFormat.BULLET,
          text: '•',
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
            },
          },
        },
        {
          level: 1,
          format: LevelFormat.BULLET,
          text: '◦',
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.75), hanging: convertInchesToTwip(0.25) },
            },
          },
        },
      ],
    },
  ],
}

export async function exportDocumentationToDocx(content: string) {
  // Parse the content into sections
  const sections = parseTrainingGuide(content)
  
  // Create document sections
  const docSections: Paragraph[] = []
  
  // Add title page
  docSections.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'PurpleTree HR Admin',
          font: DEFAULT_FONT,
          size: 48, // 24pt
          bold: true,
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 200,
      },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'User Training Guide',
          font: DEFAULT_FONT,
          size: 36, // 18pt
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 400,
      },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated on ${new Date().toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}`,
          font: DEFAULT_FONT,
          size: DEFAULT_SIZE,
          italics: true,
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: {
        after: 800,
      },
    })
  )
  
  // Add table of contents header
  docSections.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Table of Contents',
          font: DEFAULT_FONT,
          size: 32, // 16pt
          bold: true,
        })
      ],
      heading: HeadingLevel.HEADING_1,
      spacing: {
        before: 400,
        after: 300,
      },
    })
  )
  
  // Add table of contents entries
  sections.forEach((section, sectionIndex) => {
    docSections.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${sectionIndex + 1}. ${section.title}`,
            font: DEFAULT_FONT,
            size: DEFAULT_SIZE,
            bold: true,
          })
        ],
        spacing: {
          before: 100,
          after: 50,
        },
      })
    )
    
    section.articles.forEach((article, articleIndex) => {
      docSections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `    ${sectionIndex + 1}.${articleIndex + 1} ${article.title}`,
              font: DEFAULT_FONT,
              size: 20, // 10pt
            })
          ],
          spacing: {
            after: 30,
          },
        })
      )
    })
  })
  
  // Add page break after TOC
  docSections.push(
    new Paragraph({
      pageBreakBefore: true,
    })
  )
  
  // Process each section
  sections.forEach((section, sectionIndex) => {
    // Add section title
    docSections.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${sectionIndex + 1}. ${section.title}`,
            font: DEFAULT_FONT,
            size: 32, // 16pt
            bold: true,
          })
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: {
          before: 400,
          after: 200,
        },
      })
    )
    
    // Process each article in the section
    section.articles.forEach((article, articleIndex) => {
      // Add article title
      docSections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${sectionIndex + 1}.${articleIndex + 1} ${article.title}`,
              font: DEFAULT_FONT,
              size: 28, // 14pt
              bold: true,
            })
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: {
            before: 300,
            after: 150,
          },
        })
      )
      
      // Process article content
      const processedParagraphs = processMarkdownContent(article.content)
      docSections.push(...processedParagraphs)
    })
  })
  
  // Create the document
  const doc = new Document({
    numbering: numberingConfig,
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(1),
            right: convertInchesToTwip(1),
            bottom: convertInchesToTwip(1),
            left: convertInchesToTwip(1),
          },
        },
      },
      children: docSections,
    }],
    creator: 'PurpleTree HR Admin',
    title: 'User Training Guide',
    description: 'Comprehensive training guide for the PurpleTree HR Admin system',
  })
  
  // Generate and save the document
  const blob = await Packer.toBlob(doc)
  const fileName = `PurpleTree_User_Guide_${new Date().toISOString().split('T')[0]}.docx`
  saveAs(blob, fileName)
}

// Process markdown content into properly formatted paragraphs
function processMarkdownContent(content: string): Paragraph[] {
  const paragraphs: Paragraph[] = []
  const lines = content.split('\n')
  
  let inList = false
  let listLevel = 0
  let inCodeBlock = false
  let codeBlockLines: string[] = []
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()
    
    // Skip empty lines unless in code block
    if (!trimmedLine && !inCodeBlock) {
      inList = false
      continue
    }
    
    // Handle code blocks
    if (trimmedLine.startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        if (codeBlockLines.length > 0) {
          paragraphs.push(createCodeBlock(codeBlockLines.join('\n')))
        }
        codeBlockLines = []
        inCodeBlock = false
      } else {
        // Start code block
        inCodeBlock = true
      }
      continue
    }
    
    if (inCodeBlock) {
      codeBlockLines.push(line)
      continue
    }
    
    // Handle different markdown elements
    if (trimmedLine.startsWith('### ')) {
      inList = false
      paragraphs.push(createHeading3(trimmedLine.substring(4)))
    } else if (trimmedLine.startsWith('#### ')) {
      inList = false
      paragraphs.push(createHeading4(trimmedLine.substring(5)))
    } else if (trimmedLine.startsWith('##### ')) {
      inList = false
      paragraphs.push(createHeading5(trimmedLine.substring(6)))
    } else if (trimmedLine.startsWith('---')) {
      inList = false
      paragraphs.push(createHorizontalRule())
    } else if (trimmedLine.match(/^[-*•]\s+/)) {
      // Bullet list
      const bulletContent = trimmedLine.replace(/^[-*•]\s+/, '')
      const indent = line.search(/\S/)
      const level = Math.floor(indent / 2)
      paragraphs.push(createBulletPoint(bulletContent, level))
      inList = true
    } else if (trimmedLine.match(/^\d+\.\s+/)) {
      // Numbered list
      const numberContent = trimmedLine.replace(/^\d+\.\s+/, '')
      paragraphs.push(createNumberedPoint(numberContent))
      inList = true
    } else if (trimmedLine.startsWith('- [ ]') || trimmedLine.startsWith('- [x]')) {
      // Checkbox list
      const isChecked = trimmedLine.startsWith('- [x]')
      const checkboxContent = trimmedLine.substring(5).trim()
      paragraphs.push(createCheckbox(checkboxContent, isChecked))
      inList = true
    } else if (trimmedLine.startsWith('>')) {
      // Blockquote
      inList = false
      const quoteContent = trimmedLine.substring(1).trim()
      paragraphs.push(createBlockquote(quoteContent))
    } else if (trimmedLine.startsWith('|')) {
      // Table - collect all table rows
      const tableRows: string[] = [trimmedLine]
      while (i + 1 < lines.length && lines[i + 1].trim().startsWith('|')) {
        i++
        tableRows.push(lines[i].trim())
      }
      paragraphs.push(...createTable(tableRows))
      inList = false
    } else {
      // Regular paragraph
      inList = false
      paragraphs.push(createFormattedParagraph(trimmedLine))
    }
  }
  
  return paragraphs
}

// Create heading level 3
function createHeading3(text: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: sanitizeText(text),
        font: DEFAULT_FONT,
        size: 26, // 13pt
        bold: true,
      })
    ],
    heading: HeadingLevel.HEADING_3,
    spacing: {
      before: 240,
      after: 120,
    },
  })
}

// Create heading level 4
function createHeading4(text: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: sanitizeText(text),
        font: DEFAULT_FONT,
        size: 24, // 12pt
        bold: true,
      })
    ],
    heading: HeadingLevel.HEADING_4,
    spacing: {
      before: 200,
      after: 100,
    },
  })
}

// Create heading level 5
function createHeading5(text: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: sanitizeText(text),
        font: DEFAULT_FONT,
        size: DEFAULT_SIZE,
        bold: true,
        italics: true,
      })
    ],
    spacing: {
      before: 160,
      after: 80,
    },
  })
}

// Create horizontal rule
function createHorizontalRule(): Paragraph {
  return new Paragraph({
    border: {
      bottom: {
        color: 'CCCCCC',
        space: 1,
        style: BorderStyle.SINGLE,
        size: 6,
      },
    },
    spacing: {
      before: 200,
      after: 200,
    },
  })
}

// Create bullet point
function createBulletPoint(text: string, level: number = 0): Paragraph {
  const runs = parseInlineFormatting(text)
  return new Paragraph({
    children: runs,
    numbering: {
      reference: 'bullet-list',
      level: Math.min(level, 1),
    },
    spacing: {
      after: 60,
    },
  })
}

// Create numbered point
function createNumberedPoint(text: string): Paragraph {
  const runs = parseInlineFormatting(text)
  return new Paragraph({
    children: runs,
    numbering: {
      reference: 'numbered-list',
      level: 0,
    },
    spacing: {
      after: 60,
    },
  })
}

// Create checkbox
function createCheckbox(text: string, isChecked: boolean): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: isChecked ? '☑ ' : '☐ ',
        font: DEFAULT_FONT,
        size: DEFAULT_SIZE,
      }),
      ...parseInlineFormatting(text),
    ],
    spacing: {
      after: 60,
    },
    indent: {
      left: convertInchesToTwip(0.25),
    },
  })
}

// Create blockquote
function createBlockquote(text: string): Paragraph {
  const runs = parseInlineFormatting(text)
  return new Paragraph({
    children: runs,
    spacing: {
      after: 100,
    },
    indent: {
      left: convertInchesToTwip(0.5),
    },
    shading: {
      fill: 'F0F0F0',
    },
  })
}

// Create code block
function createCodeBlock(code: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: code,
        font: 'Courier New',
        size: 20, // 10pt
      })
    ],
    spacing: {
      before: 100,
      after: 100,
    },
    indent: {
      left: convertInchesToTwip(0.25),
    },
    shading: {
      fill: 'F5F5F5',
    },
  })
}

// Create table from markdown table rows
function createTable(rows: string[]): Paragraph[] {
  // For now, convert table to formatted text
  // Full table implementation would require Table elements
  const paragraphs: Paragraph[] = []
  
  rows.forEach((row, index) => {
    if (index === 1 && row.includes('---')) return // Skip separator row
    
    const cells = row.split('|').filter(cell => cell.trim())
    const isHeader = index === 0
    
    paragraphs.push(new Paragraph({
      children: [
        new TextRun({
          text: cells.join('  |  '),
          font: DEFAULT_FONT,
          size: DEFAULT_SIZE,
          bold: isHeader,
        })
      ],
      spacing: {
        after: isHeader ? 60 : 40,
      },
    }))
  })
  
  return paragraphs
}

// Create formatted paragraph with inline styles
function createFormattedParagraph(text: string): Paragraph {
  const runs = parseInlineFormatting(text)
  return new Paragraph({
    children: runs,
    spacing: {
      after: 120,
    },
  })
}

// Parse inline formatting (bold, italic, code)
function parseInlineFormatting(text: string): TextRun[] {
  const runs: TextRun[] = []
  
  // Remove markdown links [text](url) -> text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  
  // Process the text for formatting
  let remaining = text
  const patterns = [
    { regex: /\*\*([^*]+)\*\*/g, style: { bold: true } },           // **bold**
    { regex: /_([^_]+)_/g, style: { italics: true } },              // _italic_
    { regex: /\*([^*]+)\*/g, style: { italics: true } },            // *italic*
    { regex: /`([^`]+)`/g, style: { font: 'Courier New' } },        // `code`
    { regex: /~~([^~]+)~~/g, style: { strike: true } },             // ~~strike~~
  ]
  
  // Split text by all formatting patterns
  const segments: { text: string; style?: any }[] = []
  let lastIndex = 0
  
  // First pass: collect all formatted segments
  const allMatches: { index: number; length: number; text: string; style: any }[] = []
  
  patterns.forEach(pattern => {
    const regex = new RegExp(pattern.regex)
    let match
    while ((match = regex.exec(text)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        text: match[1],
        style: pattern.style,
      })
    }
  })
  
  // Sort matches by index
  allMatches.sort((a, b) => a.index - b.index)
  
  // Build segments
  allMatches.forEach(match => {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index) })
    }
    segments.push({ text: match.text, style: match.style })
    lastIndex = match.index + match.length
  })
  
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex) })
  }
  
  // Create text runs
  if (segments.length === 0) {
    runs.push(new TextRun({
      text: sanitizeText(text),
      font: DEFAULT_FONT,
      size: DEFAULT_SIZE,
    }))
  } else {
    segments.forEach(segment => {
      runs.push(new TextRun({
        text: sanitizeText(segment.text),
        font: segment.style?.font || DEFAULT_FONT,
        size: DEFAULT_SIZE,
        bold: segment.style?.bold,
        italics: segment.style?.italics,
        strike: segment.style?.strike,
      }))
    })
  }
  
  return runs
}

// Sanitize text by removing remaining markdown artifacts
function sanitizeText(text: string): string {
  return text
    .replace(/\*\*/g, '')     // Remove bold markers
    .replace(/__/g, '')       // Remove bold markers
    .replace(/\*/g, '')       // Remove italic markers
    .replace(/_/g, '')        // Remove italic markers  
    .replace(/~~/g, '')       // Remove strike markers
    .replace(/`/g, '')        // Remove code markers
    .replace(/#+\s/g, '')     // Remove heading markers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
    .replace(/&nbsp;/g, ' ')  // Replace HTML spaces
    .replace(/&lt;/g, '<')    // Replace HTML less than
    .replace(/&gt;/g, '>')    // Replace HTML greater than
    .replace(/&amp;/g, '&')   // Replace HTML ampersand
    .replace(/\u26a0\ufe0f/g, '⚠️') // Ensure emoji display
    .trim()
}